import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { CornerDownLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AssetReturns({ user, onLogout }) {
  const navigate = useNavigate();
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    asset_id: '',
    return_date: new Date().toISOString().split('T')[0],
    condition_on_return: 'Good',
    notes: ''
  });

  useEffect(() => {
    fetchAssignedAssets();
  }, []);

  const fetchAssignedAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assets?status=Assigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignedAssets(response.data.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assets/return`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Asset returned successfully!');
      setFormData({
        asset_id: '',
        return_date: new Date().toISOString().split('T')[0],
        condition_on_return: 'Good',
        notes: ''
      });
      fetchAssignedAssets();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const selectedAsset = assignedAssets.find(a => a.id === formData.asset_id);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Asset Returns</h1>
          <p className="text-gray-600 mt-2">Process asset returns from employees</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Asset Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Select Asset to Return *</label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => setFormData({...formData, asset_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Choose an assigned asset</option>
                  {assignedAssets.map(asset => (
                    <option key={asset.id} value={asset.id}>
                      {asset.asset_id} - {asset.asset_name} (Assigned to: {asset.assigned_to})
                    </option>
                  ))}
                </select>
                {assignedAssets.length === 0 && (
                  <p className="text-sm text-orange-600 mt-1">No assigned assets to return.</p>
                )}
              </div>

              {/* Asset Details Preview */}
              {selectedAsset && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-bold mb-2" style={{color: '#2b3380'}}>Asset Details</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Asset ID:</span>
                      <p className="font-medium">{selectedAsset.asset_id}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <p className="font-medium">{selectedAsset.asset_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Type:</span>
                      <p className="font-medium">{selectedAsset.asset_type}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Assignment Date:</span>
                      <p className="font-medium">{new Date(selectedAsset.assignment_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Return Date */}
              <div>
                <label className="block text-sm font-medium mb-2">Return Date *</label>
                <input
                  type="date"
                  value={formData.return_date}
                  onChange={(e) => setFormData({...formData, return_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>

              {/* Condition on Return */}
              <div>
                <label className="block text-sm font-medium mb-2">Condition on Return *</label>
                <select
                  value={formData.condition_on_return}
                  onChange={(e) => setFormData({...formData, condition_on_return: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="Excellent">Excellent</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                  <option value="Damaged">Damaged</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">Return Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Any issues, damages, or observations..."
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                type="submit"
                disabled={loading || assignedAssets.length === 0}
                className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: loading || assignedAssets.length === 0 ? '#9e9e9e' : '#2b3380'}}
              >
                <CornerDownLeft className="w-5 h-5" />
                {loading ? 'Processing...' : 'Process Return'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/assets')}
                className="px-6 py-3 rounded-lg border font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* Assigned Assets List */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Currently Assigned Assets ({assignedAssets.length})</h2>
          {assignedAssets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{backgroundColor: '#2b3380'}}>
                  <tr>
                    <th className="px-4 py-3 text-left text-white">Asset ID</th>
                    <th className="px-4 py-3 text-left text-white">Asset Name</th>
                    <th className="px-4 py-3 text-left text-white">Type</th>
                    <th className="px-4 py-3 text-left text-white">Assigned To</th>
                    <th className="px-4 py-3 text-left text-white">Since</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedAssets.map(asset => (
                    <tr key={asset.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{asset.asset_id}</td>
                      <td className="px-4 py-3">{asset.asset_name}</td>
                      <td className="px-4 py-3">{asset.asset_type}</td>
                      <td className="px-4 py-3">{asset.assigned_to}</td>
                      <td className="px-4 py-3">{new Date(asset.assignment_date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No assigned assets</p>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AssetReturns;